import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Button } from '@/components/common/Button';
import { projectService } from '@/services/projectService';
import { useNotificationStore } from '@/store/notificationStore';
import { Project, CreateProjectData, UpdateProjectData, CustomerTier } from '@/types/project';
import { formatAPIError } from '@/utils/errorHandlers';

interface ProjectFormProps {
  project?: Project;
  onSuccess?: (project: Project) => void;
  onCancel?: () => void;
}

const customerTierOptions = [
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium' },
  { value: 'enterprise', label: 'Enterprise' },
];

function generateProjectKey(name: string): string {
  // Remove special characters and get first 3 words
  const words = name
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return '';

  // If single word, take first 3-5 letters
  if (words.length === 1) {
    return words[0].substring(0, Math.min(5, words[0].length));
  }

  // If multiple words, take first letter of each (up to 5 words)
  return words
    .slice(0, 5)
    .map(word => word[0])
    .join('');
}

export function ProjectForm({ project, onSuccess, onCancel }: ProjectFormProps) {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((state) => state.addNotification);
  const isEditMode = !!project;

  const [formData, setFormData] = useState<CreateProjectData | UpdateProjectData>({
    name: project?.name || '',
    key: project?.key || '',
    description: project?.description || '',
    customer_name: project?.customer_name || '',
    customer_tier: project?.customer_tier || 'standard',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(isEditMode);

  // Auto-generate key when name changes (only for new projects)
  useEffect(() => {
    if (!isEditMode && !keyManuallyEdited && formData.name) {
      const generatedKey = generateProjectKey(formData.name);
      setFormData(prev => ({ ...prev, key: generatedKey }));
    }
  }, [formData.name, isEditMode, keyManuallyEdited]);

  const createMutation = useMutation({
    mutationFn: (data: CreateProjectData) => projectService.createProject(data),
    onSuccess: (data) => {
      addNotification({
        type: 'success',
        title: 'Project Created',
        message: `${data.name} has been created successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onSuccess?.(data);
    },
    onError: (error: any) => {
      const message = formatAPIError(error, 'Failed to create project');
      addNotification({
        type: 'error',
        title: 'Error',
        message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProjectData) => projectService.updateProject(project!.id, data),
    onSuccess: (data) => {
      addNotification({
        type: 'success',
        title: 'Project Updated',
        message: `${data.name} has been updated successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onSuccess?.(data);
    },
    onError: (error: any) => {
      const message = formatAPIError(error, 'Failed to update project');
      addNotification({
        type: 'error',
        title: 'Error',
        message,
      });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Project name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Project name must be at least 3 characters';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Project name must be less than 255 characters';
    }

    if (!formData.key?.trim()) {
      newErrors.key = 'Project key is required';
    } else if (!/^[A-Z0-9]{2,10}$/.test(formData.key)) {
      newErrors.key = 'Project key must be 2-10 uppercase letters/numbers';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (formData.customer_name && formData.customer_name.length > 255) {
      newErrors.customer_name = 'Customer name must be less than 255 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (isEditMode) {
      // For edit mode, only send changed fields
      const updateData: UpdateProjectData = {
        ...(formData.name !== project.name && { name: formData.name }),
        ...(formData.description !== project.description && { description: formData.description }),
        ...(formData.customer_name !== project.customer_name && { customer_name: formData.customer_name }),
        ...(formData.customer_tier !== project.customer_tier && { customer_tier: formData.customer_tier as CustomerTier }),
      };

      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        updateMutation.mutate(updateData);
      } else {
        addNotification({
          type: 'info',
          title: 'No Changes',
          message: 'No changes were made to the project.',
        });
        onCancel?.();
      }
    } else {
      // For create mode, send all required fields
      const createData: CreateProjectData = {
        name: formData.name!,
        key: formData.key!,
        description: formData.description || undefined,
        customer_name: formData.customer_name || undefined,
        customer_tier: formData.customer_tier as CustomerTier || 'standard',
      };
      createMutation.mutate(createData);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleKeyChange = (value: string) => {
    setKeyManuallyEdited(true);
    handleChange('key', value.toUpperCase());
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Project Name"
        value={formData.name || ''}
        onChange={(e) => handleChange('name', e.target.value)}
        error={errors.name}
        placeholder="e.g., Acme Corporation Support"
        required
        disabled={isLoading}
        autoFocus
      />

      <Input
        label="Project Key"
        value={formData.key || ''}
        onChange={(e) => handleKeyChange(e.target.value)}
        error={errors.key}
        helperText={isEditMode ? 'Project key cannot be changed' : 'Short identifier (2-10 uppercase letters/numbers, e.g., ACME)'}
        placeholder="e.g., ACME"
        required
        disabled={isLoading || isEditMode}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Optional project description"
          rows={3}
          disabled={isLoading}
          className="block w-full rounded-md border-gray-300 shadow-sm transition-colors focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
        />
        {errors.description && (
          <p className="mt-1 text-sm text-danger-600">{errors.description}</p>
        )}
      </div>

      <Input
        label="Customer Name"
        value={formData.customer_name || ''}
        onChange={(e) => handleChange('customer_name', e.target.value)}
        error={errors.customer_name}
        placeholder="e.g., Acme Corporation"
        disabled={isLoading}
      />

      <Select
        label="Customer Tier"
        value={formData.customer_tier || 'standard'}
        onChange={(value) => handleChange('customer_tier', value)}
        options={customerTierOptions}
        disabled={isLoading}
      />

      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
        >
          {isEditMode ? 'Update Project' : 'Create Project'}
        </Button>
      </div>
    </form>
  );
}

export default ProjectForm;
